import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Presentations = () => {
  const navigate = useNavigate();

  // Prüfen ob der Benutzer authentifiziert ist
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('portfolioAuth');
    if (!isAuthenticated) {
      // Zurück zur Startseite, wenn nicht authentifiziert
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header mit Zurück-Button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/portfolio')}
            variant="ghost"
            className="text-primary-dark hover:text-cyan-600"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zum Portfolio
          </Button>
        </div>
      </header>

      {/* Überschrift Section mit weißem Hintergrund */}
      <section className="pt-32 pb-12 px-6 bg-white">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0891B2' }}>
            Präsentationen
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-8 font-medium">
            Business Communication | Professional Design | Visual Storytelling
          </p>
          <Button
            size="lg"
            className="text-white font-semibold px-8 py-3 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{ backgroundColor: '#0891B2' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#06769C'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0891B2'}
          >
            Präsentationen ansehen
          </Button>
        </div>
      </section>

      {/* Content Section mit Text */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          {/* Hauptüberschrift */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#0891B2' }}>
              Professionelle Präsentationen für überzeugende Kommunikation
            </h2>

            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Eine professionelle Präsentation ist mehr als eine Aneinanderreihung von Folien – sie ist ein strategisches Kommunikationsinstrument, das komplexe Inhalte verständlich macht, Entscheidungen vorbereitet und Stakeholder überzeugt.
              </p>
              <p>
                Meine Präsentationsdesigns verbinden klare visuelle Strukturen mit gezieltem Storytelling. Ob für Investoren-Pitches, Strategiemeetings, Konferenzen oder interne Schulungen – jede Präsentation wird individuell auf Zielgruppe, Kontext und Botschaft abgestimmt.
              </p>
            </div>
          </div>

          {/* Leistungen */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0891B2' }}>
                Strategische Konzeption
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Zielgruppenanalyse und Botschaftsentwicklung</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Strukturierung komplexer Inhalte</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Storytelling und Dramaturgie</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Visuelle Konzeptentwicklung</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0891B2' }}>
                Professionelles Design
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Corporate Design konforme Gestaltung</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Datenvisualisierung und Infografiken</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Professionelle Bildsprache</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 font-bold">•</span>
                  <span>Einheitliches visuelles Erscheinungsbild</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Anwendungsbereiche */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#0891B2' }}>
              Anwendungsbereiche
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">Business & Strategy</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Investor Pitches</li>
                  <li>• Strategiepräsentationen</li>
                  <li>• Quartalsergebnisse</li>
                  <li>• Board Meetings</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">Marketing & Sales</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Produktpräsentationen</li>
                  <li>• Sales Decks</li>
                  <li>• Kundenpräsentationen</li>
                  <li>• Kampagnen-Pitches</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 text-gray-800">Events & Schulungen</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Konferenzvorträge</li>
                  <li>• Webinare</li>
                  <li>• Schulungsmaterialien</li>
                  <li>• Workshop-Unterlagen</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Beispiel Nordica Coffee */}
          <div className="mb-12">
            <h3 className="text-3xl font-bold mb-8" style={{ color: '#0891B2' }}>
              Beispiel Nordica Coffee
            </h3>

            {/* Neue Bilder direkt unter der Überschrift */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/14btkykr_Bildschirmfoto%202026-06-19%20um%2015.24.15.png"
                alt="Präsentation 1"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/ap477eav_Bildschirmfoto%202026-06-19%20um%2015.24.39.png"
                alt="Präsentation 2"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/xxxwox9p_Bildschirmfoto%202026-06-19%20um%2015.24.59.png"
                alt="Präsentation 3"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            {/* Nordica Bilder darunter */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/reut0qha_Bildschirmfoto%202026-02-24%20um%2011.05.26.png"
                alt="Nordica Landing Page - Dein täglicher Impact-Kaffee"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/lcdy207j_Bildschirmfoto%202026-02-24%20um%2011.06.08.png"
                alt="Nordica Landing Page - Rewards"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/s17hwwt6_Bildschirmfoto%202026-02-24%20um%2011.06.48.png"
                alt="Nordica Landing Page - Unsere Bestseller"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/tfx1bxr3_Bildschirmfoto%202026-02-24%20um%2011.07.06.png"
                alt="Nordica Landing Page - Warum Nordica"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-500 to-blue-900">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Mehr Projekte entdecken?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Entdecken Sie weitere spannende Projekte aus meinem Portfolio.
          </p>
          <Button
            onClick={() => navigate('/portfolio')}
            size="lg"
            className="bg-white hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
            style={{ color: '#0891B2' }}
          >
            Zurück zum Portfolio
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Presentations;
